import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDay } from '../../i18n';
import StagePill from './StagePill';
import ShowMoreButton from '../shared/ShowMoreButton';

/** The rows of one page. A long history shows "Show more" (#631, F7). */
const PAGE_SIZE = 20;

/** The fields of a bout that the row shell reads. */
export interface BoutHistoryItem {
  id: string;
  bout_number: number;
  bout_date: string | null;
  tournament_id: string;
  tournament_name: string;
  tournament_date: string;
  age_group: string | null;
  weight_class: string | null;
  stage: string | null;
}

/** The outcome and the round in one pill text: "WP · R3", "KO · R1" (#631, B).
 *  The outcome codes stay English. */
export function outcomeWithRound(outcome: string | null, round: number | null): string | null {
  if (!outcome) return null;
  return round ? `${outcome} · R${round}` : outcome;
}

/** #631 — the bout list of the athlete page and the official page: one row
 *  shell for both (#615 made the official row a copy of the athlete row). The
 *  shell draws the date and the tournament, the bout number, the category
 *  pills and the paging. Each page gives the corner line and its own pills:
 *  `pills` is the first group; `contextPills` comes before the category pills
 *  in the second group. On a narrow screen the second group goes under the
 *  first. */
export default function BoutHistoryList<T extends BoutHistoryItem>({
  title, bouts, empty, hint, rowTestId, rowAttrs, corners, pills, contextPills,
}: {
  title: string;
  bouts: T[];
  empty: string;
  /** The info lines under the title. */
  hint?: ReactNode;
  rowTestId: string;
  rowAttrs?: (b: T) => Record<string, string>;
  corners: (b: T) => ReactNode;
  pills: (b: T) => ReactNode;
  contextPills?: (b: T) => ReactNode;
}) {
  const [shown, setShown] = useState(PAGE_SIZE);

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 font-semibold text-sm">{title} ({bouts.length})</div>
      {hint}
      {bouts.length === 0 ? (
        <p className="p-4 text-sm text-gray-400 text-center">{empty}</p>
      ) : (
        <>
          <ul className="divide-y divide-gray-100">
            {bouts.slice(0, shown).map(b => {
              const context = contextPills?.(b);
              return (
              <li key={b.id} data-testid={rowTestId} data-bout-id={b.id} {...rowAttrs?.(b)} className="p-3 sm:px-4 space-y-1.5">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-gray-500">
                      {formatDay(b.bout_date ?? b.tournament_date, 'dd MMM yyyy')} · <Link to={`/tournaments/${b.tournament_id}`} className="hover:underline">{b.tournament_name}</Link>
                    </div>
                    {/* #552 — a name wraps on a phone and is never cut. */}
                    <div className="text-sm flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">{corners(b)}</div>
                  </div>
                  <Link to={`/bouts/${b.id}`} className="btn-outline btn-sm shrink-0">#{b.bout_number}</Link>
                </div>
                {/* The pills of the page, then the category: the pills of the
                    Boxers tab (the age group grey, the weight class amber) and
                    the stage in the colours of the live rows. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <div className="flex flex-wrap items-center gap-1.5">{pills(b)}</div>
                  {(context || b.age_group || b.weight_class || b.stage) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {context}
                      {b.age_group && <span className="badge badge-gray whitespace-nowrap">{b.age_group}</span>}
                      {b.weight_class && <span className="badge badge-amber whitespace-nowrap">{b.weight_class}</span>}
                      <StagePill stage={b.stage} />
                    </div>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
          <ShowMoreButton shown={shown} total={bouts.length} onMore={() => setShown(shown + PAGE_SIZE)} testId="bout-history-more" />
        </>
      )}
    </div>
  );
}
